import React, { forwardRef, TextareaHTMLAttributes, useRef, useEffect, ChangeEvent, useState } from 'react';

import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { cn } from 'utils/global';

import { ErrorMessage } from './index';

export interface Props extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  textAreaClassName?: string;
  labelClassName?: string;
  anchorPrefix?: string;
  error?: string;
  isAbsoluteError?: boolean;
  placeholder?: string;
  numberOfCharacters?: number;
  value?: string;
  dataCy?: string;
  required?: boolean;
  inputClassName?: string;
}

const TEXT_AREA_HEIGHT_OFFSET = 1;

const TextArea = forwardRef<HTMLTextAreaElement, Props>(
  (
    {
      label,
      labelClassName,
      textAreaClassName,
      className,
      error,
      isAbsoluteError,
      onChange,
      placeholder,
      numberOfCharacters,
      value,
      dataCy,
      required,
      inputClassName,
      ...textAreaProps
    },
    ref
  ) => {
    const localRef = useRef<HTMLTextAreaElement | null>(null);
    const [isResizedManually, setIsResizedManually] = useState(false);

    const { watch } = useFormContext();
    const { t } = useTranslation();
    const watchValue = watch(textAreaProps.name as string);

    const setRef = (node: HTMLTextAreaElement) => {
      if (!node) {
        return;
      }

      if (typeof ref === 'function') {
        ref(node);
      } else {
        (ref as React.MutableRefObject<HTMLTextAreaElement | null>).current = node;
      }

      localRef.current = node;
    };

    const handleManualResize = () => {
      setIsResizedManually(true);
    };

    const handleChange = (ev: ChangeEvent<HTMLTextAreaElement>) => {
      if (onChange) {
        onChange(ev);
      }

      if (localRef.current && !isResizedManually) {
        localRef.current.style.height = 'auto';
        localRef.current.style.height = `${ev.target.scrollHeight + TEXT_AREA_HEIGHT_OFFSET}px`;
      }
    };

    useEffect(() => {
      if (!localRef.current) {
        return undefined;
      }

      const textArea = localRef.current;
      textArea.addEventListener('mouseup', handleManualResize);

      return () => textArea.removeEventListener('mouseup', handleManualResize);
    }, []);

    return (
      <div className={className} data-cy={dataCy}>
        {label && (
          <div className={cn('text-neutral-900 dark:text-neutral-100 text-bodyMd-semibold mb-1', labelClassName)}>
            {t(label)}
            {label && required && ' *'}
          </div>
        )}

        <div className="relative">
          <textarea
            value={value}
            ref={setRef}
            onChange={handleChange}
            placeholder={placeholder}
            maxLength={numberOfCharacters}
            className={cn(
              'custom-textarea !text-bodyMd dark:text-white rounded-md',
              {
                '!border-red-500 rounded': !!error,
              },
              textAreaClassName,
              inputClassName
            )}
            {...textAreaProps}
          />
        </div>

        {error && (
          <ErrorMessage
            dataCy="error-message"
            className={cn('input-error-message text-right mt-[-1]', {
              absolute: isAbsoluteError,
            })}
          >
            {t(error)}
          </ErrorMessage>
        )}

        {numberOfCharacters ? (
          <div className="w-fit rounded-2xl bg-neutral-300 dark:bg-neutral-700 px-2 text-neutral-800 dark:text-neutral-200 text-sm">
            {watchValue?.length || 0} / {numberOfCharacters}
          </div>
        ) : null}
      </div>
    );
  }
);

export default TextArea;

TextArea.displayName = 'TextArea';
